import * as React from "react";
import { cn } from "../../lib/utils";

interface EmptyStateProps extends Omit<React.ComponentProps<"div">, "title"> {
  icon?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  /** `page` is roomy for primary empty screens; `inline` fits inside cards and tabs. */
  size?: "page" | "inline";
}

function EmptyState({
  icon,
  title,
  description,
  action,
  size = "inline",
  className,
  ...props
}: EmptyStateProps) {
  return (
    <div
      data-slot="empty-state"
      className={cn(
        "flex flex-col items-center justify-center text-center animate-fade-in",
        size === "page" ? "py-20 px-6" : "py-12 px-6",
        className,
      )}
      {...props}
    >
      {icon && (
        <div className="relative mb-5">
          <div
            aria-hidden
            className="absolute inset-0 -m-3 rounded-full border border-primary/10"
          />
          <div
            aria-hidden
            className="absolute inset-0 -m-7 rounded-full border border-primary/[0.06]"
          />
          <div className="relative flex size-14 items-center justify-center rounded-2xl bg-primary-soft text-primary [&>svg]:size-6">
            {icon}
          </div>
        </div>
      )}
      <h3
        className={cn(
          "font-display font-semibold tracking-tight",
          size === "page" ? "text-xl" : "text-base",
        )}
      >
        {title}
      </h3>
      {description && (
        <p
          className={cn(
            "mt-2 max-w-md text-text-secondary",
            size === "page" ? "text-[15px]" : "text-sm",
          )}
        >
          {description}
        </p>
      )}
      {action && <div className="mt-6 flex items-center gap-3">{action}</div>}
    </div>
  );
}

export { EmptyState };
